import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useIntl } from "react-intl";
import {
  AdminDeliveryMethod,
  AdminSkuDeliverableRequest,
  IAdminSkuDeliverableRequest,
  IMediaItem,
  SaveSkuDeliverableRequest,
  Skus,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import SkuDeliveryMethodCommonForm from "./SkuDeliveryMethodCommonForm";
import { DialogContent } from "@mui/material";
import SelectDeliveryMethod from "components/SelectDeliveryMethod";
import { useState } from "react";
import SkuDeliveryMediaItemCreateForm, {
  mediaItemFormPropsMap,
} from "./SkuDeliveryMediaItemCreateForm";

type SkuDeliveryMethodCreateProps = {
  sku: Skus;
  onCancel?: () => void;
  onSkuDeliveryMethodCreated: () => void;
  skuDeliveryMethodsMaxSequence: number | undefined;
};

const SkuDeliveryMethodCreateForm = ({
  sku,
  onCancel,
  onSkuDeliveryMethodCreated,
  skuDeliveryMethodsMaxSequence,
  ...rest
}: SkuDeliveryMethodCreateProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { skuService, skuDeliverableService } = useGlobalContext();
  const [
    deliveryMethodSelected,
    setDeliveryMethodSelected,
  ] = useState<AdminDeliveryMethod | null>(null);

  const onCommonFormSubmit = async (
    values: IAdminSkuDeliverableRequest
  ) => {
    try {
      if (skuDeliveryMethodsMaxSequence) {
        values.sequence = skuDeliveryMethodsMaxSequence + 1;
      }

      const response = await registerPromise(
        skuService.saveSkuDeliveryMethod(
          sku.id!,
          AdminSkuDeliverableRequest.fromJS(values)
        )
      );

      onSkuDeliveryMethodCreated();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSubmit = async (item: IMediaItem) => {
    await skuDeliverableService.saveSkuDeliverable(
      SaveSkuDeliverableRequest.fromJS({
        skuId: sku.id,
        deliverableId: item.id,
        sequence:
          skuDeliveryMethodsMaxSequence !== undefined
            ? skuDeliveryMethodsMaxSequence + 1
            : undefined,
      })
    );

    onSkuDeliveryMethodCreated();
  };

  const mediaItemFormProps =
    deliveryMethodSelected?.id &&
    mediaItemFormPropsMap[deliveryMethodSelected.id];

  return (
    <>
      <DialogContent>
        <SelectDeliveryMethod
          label={t({ id: "skuDeliveryMethodTitle" })}
          onChange={(dm) => setDeliveryMethodSelected(dm)}
          value={deliveryMethodSelected?.id ?? ""}
          required
        />
      </DialogContent>

      {!!deliveryMethodSelected?.id && !mediaItemFormProps && (
        <SkuDeliveryMethodCommonForm
          onSubmit={onCommonFormSubmit}
          onCancel={onCancel}
          sku={sku}
          submitLabel={t({ id: "skuDeliveryMethodSaveButtonLabel" })}
          deliveryMethodSelected={deliveryMethodSelected}
          {...rest}
        />
      )}

      {!!deliveryMethodSelected?.id && !!mediaItemFormProps && (
        <SkuDeliveryMediaItemCreateForm
          {...mediaItemFormProps}
          organizationId={sku.ownerOrganizationId!}
          onSubmit={onSubmit}
          onCancel={() => onCancel && onCancel()}
        />
      )}
    </>
  );
};

export default SkuDeliveryMethodCreateForm;
